import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { CandidateEntityConstant } from "@workspace/candidate/src/constants/candidateConstant";
import { ObjectHelper } from "@workspace/common";
import { putCandidate } from "@workspace/candidate/src/store/candidate/action";
import { Formik, Form } from "formik";
import CreatableSelect from "react-select/creatable";
import "./CandidateSkillsDetailsSection.scss";

export const CandidateSkillsDetailsSection = ({
  id,
  candidate,
  publicView,
}) => {
  const dispatch = useDispatch();

  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [candidateData, setCandidateData] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);

  // Creatable Components
  const creatableComponents = {
    DropdownIndicator: null,
  };

  // Set Submission Data
  useEffect(() => {
    if (candidate) {
      setCandidateData(candidate?.candidateSubmissionData);
    }
  }, [candidate]);

  // Set Options for Creatable Select
  useEffect(() => {
    if (candidateData?.primarySkill) {
      const options = candidateData?.primarySkill?.split(",").map((skill) => ({
        value: skill,
        label: skill,
      }));
      setDefaultOptions(options);
      setSelectedOptions(options);
    }
  }, [candidateData]);

  // Styling for Creatable Select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "1px solid #E7E9EB",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(50, 86, 237, 0.2)" : "none", // Focus shadow
      "&:hover": {
        border: "1px solid #E7E9EB",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#3256ED", // Background color for selected values
      borderRadius: "12px", // Rounded corners for selected values
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#fff",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#fff",
      "&:hover": {
        backgroundColor: "#3256ED", // Hover background color for the "x" button
        color: "#fff", // Hover text color for the "x" button
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3256ED" // Background color for selected option
        : state.isFocused
        ? "#E7E9EB" // Background color for focused option
        : "#fff", // Default background color
      color: state.isSelected ? "#fff" : "#000", // Text color for options
      "&:hover": {
        backgroundColor: "#E7E9EB", // Hover background color for options
      },
    }),
  };

  // Key Actions for Creatable Select
  const handleKeyDown = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case "Enter":
      case "Tab":
        setSelectedOptions((prev) => [
          ...prev,
          { value: inputValue, label: inputValue },
        ]);
        setInputValue("");
        event.preventDefault();
    }
  };

  // Update Primary Skills
  const handleSubmit = (values) => {
    const primarySkills = selectedOptions
      ?.map((value) => value.value)
      .join(",");
    const updatedValues = { ...values, primarySkill: primarySkills };

    const savedCandidateValues = {
      ...updatedValues,
      formData: JSON.stringify(updatedValues),
      formId: parseInt(candidate?.formId),
    };

    const formData = ObjectHelper.convertObjectToFormData(savedCandidateValues);

    dispatch(
      putCandidate({
        entity: CandidateEntityConstant.CANDIDATE_BASIC_INFO,
        id: id,
        newData: formData,
        config: {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      })
    );
  };

  // Toggle Edit Mode
  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  return (
    <>
      <div className="d-flex flex-row justify-content-start gap-3 align-items-center fw-semibold mb-2">
        <span
          className={`mdi ${
            isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
          } cursor-pointer`}
          onClick={() => setIsExpanded(!isExpanded)}
        ></span>
        <span>My Skills</span>
      </div>
      <div
        className={`transition-container mb-3 ${
          isExpanded ? "expanded" : "collapsed"
        }`}
      >
        {candidateData?.primarySkill &&
        candidateData?.primarySkill?.length > 0 ? (
          <div className="row d-flex align-items-center flex-row">
            <div className="col-11 d-flex flex-row align-items-center gap-2">
              {candidateData?.primarySkill?.split(",").map((skill, index) => (
                <div key={index}>
                  <span className="skill-portfolio-single">{skill}</span>
                </div>
              ))}
            </div>
            <div className="col-1 d-flex justify-content-end">
              <div
                style={{
                  backgroundColor: "#E7E9EB",
                  color: "#000",
                  width: "22px",
                  height: "22px",
                  visibility: publicView ? "hidden" : "visible",
                }}
                onClick={toggleEdit}
                className="rounded-circle d-flex justify-content-center align-items-center cursor-pointer"
              >
                <span className="mdi mdi-pencil"></span>
              </div>
            </div>
            <div
              className={`transition-container ${
                isEditing ? "expanded" : "collapsed"
              }`}
            >
              <Formik initialValues={candidateData} enableReinitialize>
                {({ values, resetForm }) => {
                  return (
                    <Form className="mt-3">
                      <span className="text-muted">Editing My Skills</span>
                      <div className="mt-2">
                        <CreatableSelect
                          isClearable
                          isMulti
                          onKeyDown={handleKeyDown}
                          menuIsOpen={false}
                          value={selectedOptions}
                          inputValue={inputValue}
                          onInputChange={(value) => setInputValue(value)}
                          onChange={(value) => setSelectedOptions(value)}
                          components={creatableComponents}
                          placeholder="Add Skills"
                          styles={customStyles}
                        />
                      </div>
                      <div className="d-flex flex-row justify-content-start gap-3 mt-3">
                        <div
                          className="cursor-pointer"
                          style={{ color: "#10B967" }}
                          onClick={() => {
                            handleSubmit(values);
                            setIsEditing(false);
                          }}
                        >
                          <span className="mdi mdi-check me-2"></span>
                          <span>Save</span>
                        </div>
                        <div
                          className="cursor-pointer"
                          style={{ color: "#C71010" }}
                          onClick={() => {
                            resetForm();
                            setSelectedOptions(defaultOptions);
                            setIsEditing(false);
                          }}
                        >
                          <span className="mdi mdi-close me-2"></span>
                          <span>Cancel</span>
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        ) : (
          <div className="d-flex justify-content-start">
            <span className="text-muted">No skills found.</span>
          </div>
        )}
      </div>
      <hr />
    </>
  );
};
